import { Client } from "@/Common/Api/Client";
import { LicencedIntervenor } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicencedIntervenor";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { Person } from "@/Common/Models/Person/Person";
import * as ServerOperations from "@/Common/Operations/operations.server";

interface LicencedDoctorAutocompleteProps {
    focusOnMount?: boolean;
    searchOnMount?: boolean;
    componentId: string;
    text: string;
    defaultText?: string;
    isValid?: boolean;
    maxLength?: number;
    getClinicianDoctors: () => Person[];
    onItemSelectCallback: (intervenor: LicencedIntervenor) => void;
    onBlur?: () => void;
    filterSearchResult?: (intervenors: LicencedIntervenor[]) => LicencedIntervenor[];
    textChangedFunction?: (s: string) => void;
}

class LicencedDoctorAutocompleteState {
    public results: LicencedIntervenor[];
    public isListDisplayed: boolean;
    public text: string;
    public isFocused: boolean;
    public isSearching: boolean;

    constructor(fields?: Partial<LicencedDoctorAutocompleteState>) {
        Object.assign(this, fields);
    }

    public static default(text: string = ""): LicencedDoctorAutocompleteState {
        return new LicencedDoctorAutocompleteState({
            results: [],
            isListDisplayed: false,
            text,
            isFocused: false,
            isSearching: false
        });
    }
}

class LicencedDoctorAutocomplete extends BaseReactComponent<LicencedDoctorAutocompleteProps, LicencedDoctorAutocompleteState> {
    private timeoutReference: any;
    private debounceMs: number = 400;
    private containerRef: HTMLDivElement;

    public constructor(props: LicencedDoctorAutocompleteProps) {
        super(props);
        this.handleClickOutSide = this.handleClickOutSide.bind(this);

        const initialText = this.applyMaxLength(props.defaultText ?? "");
        this.state = LicencedDoctorAutocompleteState.default(initialText);
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        document.removeEventListener("click", this.handleClickOutSide, false);
    }

    public componentDidMount(): void {
        if (this.props.searchOnMount) {
            this.executeSearch();
        }
        document.addEventListener("click", this.handleClickOutSide);
    }

    public componentDidUpdate(prevProps: LicencedDoctorAutocompleteProps): void {
        const textChanged = !this.state.isSearching && this.props.text !== prevProps.text;

        if (textChanged) {
            this.mutateState(state => state.text = this.applyMaxLength(this.props.text ?? ""));
        }
    }

    public render(): JSX.Element {
        const errorClass = this.props.isValid ? "" : " has-error";

        return (
            <div
                id={"Parentdiv_" + this.props.componentId}
                className={"doctor-autocomplete" + errorClass}
                ref={ref => this.containerRef = ref}
            >
                <input
                    id={this.props.componentId}
                    key={this.props.componentId}
                    type="text"
                    className="form-control"
                    onClick={e => { e.preventDefault(); this.onFocus(); }}
                    onKeyDown={e => this.keyDown(e)}
                    onChange={e => this.searchTextChanged(e)}
                    onBlur={() => this.props.onBlur()}
                    value={this.state.text}
                />
                {this.state.isListDisplayed && (
                    <div className="doctor-results-ps" onClick={e => e.preventDefault()}>
                        <div id="scroll_container" className="doctor-results-container">
                            <ul className="doctor-result-list" style={{ borderColor: "solid 1px #F4F6F9" }}>
                                {this.state.results.map(physician => {
                                    const cssStyle: React.CSSProperties = physician.IsHighlighted ? { backgroundColor: "#e4edfb" } : {};

                                    return (
                                        <li
                                            id={"li_doctor_" + physician.ItemId}
                                            key={"li_doctor_" + physician.ItemId}
                                            onClick={() => this.selectItem(physician)}
                                        >
                                            <div
                                                id={"doctor_" + physician.ItemId}
                                                key={"doctor_" + physician.ItemId}
                                                className="doctor-result"
                                                style={cssStyle}
                                            >
                                                <h5>{physician.getFullDisplayLabel().trim()}</h5>
                                                {!IsNullOrWhiteSpace(this.state.text) && !IsNullOrWhiteSpace(physician.Address) && (
                                                    <p>
                                                        <span>{physician.Address}</span>
                                                    </p>
                                                )}
                                                {!IsNullOrWhiteSpace(this.state.text) && (
                                                    <p>
                                                        <span>{physician.Phone}</span>
                                                        {!IsNullOrWhiteSpace(physician.Fax) && (
                                                            <span style={{ marginLeft: "17px" }}>{"Fax: " + physician.Fax}</span>
                                                        )}
                                                    </p>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private searchTextChanged(e: React.ChangeEvent<HTMLInputElement>): void {
        if (this.timeoutReference != null) {
            clearTimeout(this.timeoutReference);
            this.timeoutReference = null;
        }

        const searchText = this.applyMaxLength(e.target.value);

        this.mutateState(s => s.text = searchText);

        this.timeoutReference = window.setTimeout(
            async () => {
                await this.executeSearch();
                this.props.textChangedFunction(this.state.text);
            },
            this.debounceMs
        );
    }

    private onFocus(): void {
        if (this.state.isFocused)
            return;

        this.mutateState(
            s => s.isFocused = true,
            () => {
                if (!this.state.results.any())
                    this.executeSearch();
                else if (!this.state.isListDisplayed)
                    this.mutateState(s => s.isListDisplayed = true);
            }
        );
    }

    private handleClickOutSide(event: MouseEvent): void {
        if (event.target instanceof Element && this.containerRef?.contains?.(event.target) === false)
            this.removeFocus();
    }

    private keyDown(e: React.KeyboardEvent): void {
        // up
        if (e.keyCode === 38) { e.preventDefault(); this.moveSelectionUpOrDown(true); }

        // down
        if (e.keyCode === 40) { e.preventDefault(); this.moveSelectionUpOrDown(false); }

        // enter
        if (e.keyCode === 13) {
            if (this.indexOfSelectedItem() === -1)
                this.removeFocus();
            else
                this.selectItem(this.state.results[this.indexOfSelectedItem()]);
        }

        // escape
        if (e.keyCode === 27)
            this.mutateState(s => s.isListDisplayed = false);
    }

    private removeFocus(): void {
        this.mutateState(s => {
            s.isListDisplayed = false;
            s.isFocused = false;
        });
    }

    private moveSelectionUpOrDown(IsDirectionUp: boolean): void {
        if (this.state.results == null)
            return;

        const collection = this.state.results;
        if (collection.length == 0)
            return;

        let index = this.indexOfSelectedItem();
        if (index === -1) { // None selected
            collection.forEach(x => x.IsHighlighted = false);
            collection[IsDirectionUp ? collection.length - 1 : 0].IsHighlighted = true;
            this.mutateState(s => s.results = collection, () => this.adjustScroll());
            return;
        }

        // Top or bottom of list
        if ((index === 0 && IsDirectionUp) || (index === collection.length - 1 && !IsDirectionUp))
            return;

        collection[index].IsHighlighted = false;
        index = index + (IsDirectionUp ? -1 : 1);

        collection[index].IsHighlighted = true;
        this.mutateState(s => s.results = collection, () => this.adjustScroll());
    }

    public adjustScroll(): void {
        const idx = this.indexOfSelectedItem();

        if (idx === -1)
            return;

        let scrollParent = $("#scroll_container");
        let liSelected = $("#li_doctor_" + this.state.results?.[idx]?.ItemId);
        let firstItem = $("#li_doctor_" + this.state.results?.[0]?.ItemId);

        if (scrollParent && liSelected && firstItem) {
            let scrollParentHeight = scrollParent.height();
            let currentScrollPosition = Math.abs(firstItem.position().top);

            let selectedItemRelativePosition = liSelected.position().top; // Relative to currentScrollPosition
            let selectedItemHeight = liSelected.height();

            if (selectedItemRelativePosition < 0) // Item is above scoll position
                scrollParent.scrollTop(currentScrollPosition + selectedItemRelativePosition);
            else if (selectedItemRelativePosition + selectedItemHeight > scrollParentHeight) // Item is below scroll
                scrollParent.scrollTop(currentScrollPosition + (selectedItemRelativePosition + selectedItemHeight - scrollParentHeight)); // Add the offset to scroll position in order to fully include the last item
        }
    }

    private indexOfSelectedItem(): number {
        if (!this.state.results?.length)
            return -1;

        for (let i = 0; i < this.state.results.length; i++) {
            const item = this.state.results[i];
            if (item.IsHighlighted == true)
                return i;
        }
        return -1;
    }

    private selectItem(item: LicencedIntervenor): void {
        const intervenor = new LicencedIntervenor({
            ...item,
            LastName: item.LastName.trim(),
            FirstName: item.FirstName.trim()
        });

        this.mutateState(
            s => {
                const result = (item == null) ? "" : intervenor.Name;
                s.text = this.applyMaxLength(result);
                s.isListDisplayed = false;
                s.isFocused = false;
            },
            () => this.props.onItemSelectCallback(intervenor)
        );
    }

    private async executeSearch(): Promise<void> {
        const isSearchFieldEmpty = IsNullOrWhiteSpace(this.state.text);

        if (!isSearchFieldEmpty && this.state.text.length < 2)
            return;

        this.mutateState(
            s => {
                if (isSearchFieldEmpty) {
                    s.results = this.props.getClinicianDoctors().map(d => LicencedIntervenor.FromPerson(d));
                    this.filterSortAndDisplayResults(s);
                }
                else
                    s.isSearching = true;
            },
            async () => {
                if (isSearchFieldEmpty)
                    return;

                let result = await Client.postAsync<{ SearchText: string, SearchLicenceResults: any[] }>(
                    ServerOperations.Server.MedfarDictionnary_SearchDoctors,
                    {
                        SearchString: this.state.text,
                        MaxResults: 25,
                        StateLookUpId: sessionStore.getCurrentEmployeeActiveSiteStateLookUpId()
                    }
                );

                this.mutateState(
                    s => s.isSearching = false,
                    () => {
                        if (!result.isSuccess) {
                            console.error(result.errors);
                            return;
                        }

                        if (this.state.text === result.data.SearchText) {
                            this.mutateState(s => {
                                s.results = (result.data.SearchLicenceResults ?? []).map(result => {
                                    result.OrganisationCode = cacheStore.AllLicenseOrganizationRulesById[result.LicenceOrganisationRuleId].OrganisationCode;
                                    return new LicencedIntervenor(result);
                                });

                                this.filterSortAndDisplayResults(s);
                            });
                        }
                    }
                );
            }
        );
    }

    private filterSortAndDisplayResults(s: LicencedDoctorAutocompleteState): void {
        s.results = this.props.filterSearchResult?.(s.results) ?? s.results;

        if (s.results.any())
            s.isListDisplayed = true;
    }

    private applyMaxLength(text: string): string {
        return this.props.maxLength > 0 && text?.length > this.props.maxLength
            ? text?.substr(0, this.props.maxLength)
            : text;
    }
}

export { LicencedDoctorAutocomplete };
